// libraries
import React from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'

// state
import { showFormModal } from 'state/ui'

// generic components
import DataGridWithSearch from 'components/generic/datagrid/DataGridWithSearch'

// helpers and utils
import { setDecimalPrecision } from 'utils/helpers'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import { TRANSPORTATION_TYPE_OPTIONS, FILTER_OPTION_TYPES, CUSTOMERS_FILTERS } from 'utils/constants'
import FORM_MODALS from 'utils/ui/modals'

const useStyles = makeStyles(() => ({
  root: {
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
  },
}))

const generateColumns = (disableUmDropdowns, defaultYardUm) => [
  {
    field: 'carrier',
    headerName: 'Carrier',
    headerAlign: 'left',
    align: 'left',
    flex: 2,
  },
  {
    field: 'location',
    headerName: 'Location',
    headerAlign: 'left',
    align: 'left',
    flex: 1,
  },
  {
    field: 'transportType',
    headerName: 'Transport Type',
    headerAlign: 'left',
    align: 'left',
    flex: 1,
  },
  {
    field: 'minCost',
    headerName: 'Min Price',
    flex: 1,
    headerAlign: 'right',
    align: 'right',
    renderCell: params => `$${setDecimalPrecision(params?.row?.minCost, { decimalPrecisionOverride: 2 })}`,
  },
  {
    field: 'costPerUm',
    headerName: 'Price/UM',
    flex: 1,
    headerAlign: 'right',
    align: 'right',
    renderCell: params => {
      const { row = {} } = params
      const { costPerUm, um } = row
      return `$${setDecimalPrecision(costPerUm, { decimalPrecisionOverride: 2 })}/${disableUmDropdowns ? defaultYardUm : um}`
    },
  },
  {
    field: 'fuelSurcharge',
    headerName: 'Fuel Surcharge',
    flex: 1,
    headerAlign: 'right',
    align: 'right',
    renderCell: params => `${setDecimalPrecision(params?.row?.fuelSurcharge, { decimalPrecisionOverride: 1 })}%`,
  },
  {
    field: 'effectiveDate',
    headerName: 'Effective Date',
    type: 'date',
    headerAlign: 'left',
    align: 'left',
    flex: 1,
    renderCell: params => (params?.row?.effectiveDate ? dateFormatter(params.row.effectiveDate) : ''),
  },
  {
    field: 'expirationDate',
    headerName: 'Expiration Date',
    type: 'date',
    headerAlign: 'left',
    align: 'left',
    flex: 1,
    renderCell: params => (params?.row?.expirationDate ? dateFormatter(params.row.expirationDate) : 'No Expiration'),
  },
]

const FreightLanesGrid = ({ locationOptions, ...restProps }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const handleRowClick = params => {
    const { row } = params
    const { id, carrier, transportType, city, state } = row
    dispatch(
      showFormModal({
        formModalName: FORM_MODALS.FREIGHT_LANE_BLADE,
        formModalProps: {
          freightLaneId: id,
          freightLaneData: {
            carrier: {
              displayName: carrier,
            },
            transportationMode: transportType,
            location: {
              city,
              state,
            },
          },
        },
      }),
    )
  }

  const { settings, defaultYardUm } = useSelector(state => state.settings)
  const { disableUmDropdowns } = settings?.purchaseSettings?.inboundTickets || {}

  return (
    <DataGridWithSearch
      {...restProps}
      className={classes.root}
      textSearchPlaceholder="Search"
      columns={generateColumns(disableUmDropdowns, defaultYardUm)}
      onRowClick={handleRowClick}
      additionalFilterOptions={[
        {
          name: 'carrierId',
          label: 'Carrier',
          type: FILTER_OPTION_TYPES.CUSTOMER_AUTOCOMPLETE,
          baseFilter: CUSTOMERS_FILTERS.CARRIER,
          width: '250px',
        },
        {
          name: 'locationId',
          label: 'Location',
          type: FILTER_OPTION_TYPES.SELECT,
          options: locationOptions,
          width: '250px',
        },
        {
          name: 'transportTypeId',
          label: 'Transport Type',
          type: FILTER_OPTION_TYPES.SELECT,
          options: TRANSPORTATION_TYPE_OPTIONS,
          width: '200px',
        },
      ]}
    />
  )
}

FreightLanesGrid.propTypes = {
  ...DataGridWithSearch.propTypes,
  locationOptions: PropTypes.array,
}

FreightLanesGrid.defaultProps = {
  ...DataGridWithSearch.defaultProps,
  locationOptions: [],
}

export default FreightLanesGrid
